/**
 * Spacing tokens.
 *
 * Based on an 8px base unit (MUI default), with a 4px half-step that matches
 * the dense paddings seen in the Figma swatches and table rows.
 */

/** MUI spacing factor base (in px). `theme.spacing(1) = 8px`. */
export const spacingUnit = 8;

/** Named spacing scale in pixels. */
export const spacing = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
} as const;

export type SpacingToken = keyof typeof spacing;

/**
 * What each rung of the scale is for.
 *
 * The counterpart to `backgroundUsage`: it keeps the "which token do I reach
 * for" answer next to the values themselves, so the agent kit's tokens
 * reference and the Storybook page generate from one source instead of
 * restating a table that then drifts.
 */
export const spacingUsage = {
  none: { px: spacing.none, useFor: 'Collapse spacing explicitly.' },
  xxs: { px: spacing.xxs, useFor: 'Micro optical adjustments.' },
  xs: { px: spacing.xs, useFor: 'Icon-to-text gap, compact chip or badge padding.' },
  sm: { px: spacing.sm, useFor: 'Default element gap, small component padding.' },
  md: { px: spacing.md, useFor: 'Card padding, section gaps, form field spacing.' },
  lg: { px: spacing.lg, useFor: 'Group separation, card body padding.' },
  xl: { px: spacing.xl, useFor: 'Major section breaks, page-level gutters.' },
  xxl: { px: spacing.xxl, useFor: 'Page margins, hero spacing.' },
  xxxl: { px: spacing.xxxl, useFor: 'Large layout gaps, top-of-page offset.' },
} as const satisfies Record<SpacingToken, { px: number; useFor: string }>;
